/**
 * Coach- and athlete-facing copy for the Goal Score surfaces (PR-7a).
 *
 * SINGLE SOURCE of every string the Goal Score section, the per-Goal cards,
 * and the publish-consent control render — same discipline as
 * `checkpointCoverageCopy.ts`, which this module REUSES for the "could not
 * measure" reason text rather than duplicating it (per the PR-7a task's
 * explicit instruction: "reuse `checkpointCoverageCopy`, never duplicate
 * copy").
 *
 * Verbatim strings are drawn from, in order of authority:
 *   1. The real backend contract (`docs/API_BACKEND.md`, route code) for
 *      anything the API itself returns as a string (`consent_prompt`,
 *      `camera_framing_tip`, the hide `notice`, `fallback_reason`,
 *      `board_scope_disclosure`).
 *   2. `docs/goal_score/mockups/02_evaluation_results.html` /
 *      `03_leaderboard.html` (the v5 mockups, post-H2-curve-ruling — the
 *      ones that match the REAL `Total = Goals × Form effect` formula, not
 *      the superseded v1 "100 Form + 12 from Goals" additive mockup text).
 *   3. `docs/goal_score/REPORT.md` / `J_leaderboard_v2.md` for formula
 *      semantics where no verbatim UI string exists yet.
 *
 * `None` is NEVER rendered as `0` anywhere in this module (LFT-063) — every
 * function that could receive a null measurement returns a sentence that
 * says so in plain language instead of coercing to a number.
 */
import { withheldCheckpointReason } from './checkpointCoverageCopy';
import type { GoalAbstentionReason } from '@/types/api';
import type { GoalComparison, GoalKind, GoalScoreItem } from '@/types/goalScore';

// ---------------------------------------------------------------------------
// Units and measurement phrasing
// ---------------------------------------------------------------------------

/**
 * Plain-language unit per Goal kind, matching what the backend ACTUALLY
 * measures (`docs/API_BACKEND.md`'s Goal Score section), not the v5
 * mockup's placeholder "m/s" for Speed — the pipeline has no camera
 * calibration for a real-world unit, so Speed is body-heights-per-second,
 * exactly like Distance's own body-height ratio. Showing "m/s" would be a
 * fabricated unit the measurement cannot actually back.
 */
function unitFor(kind: GoalKind): string {
  switch (kind) {
    case 'distance':
      return 'body heights';
    case 'speed':
      return 'body heights per second';
    case 'angle':
      return '°';
  }
}

function formatMeasurement(value: number, kind: GoalKind): string {
  const rounded = kind === 'angle' ? Math.round(value) : Math.round(value * 10) / 10;
  return kind === 'angle' ? `${rounded}°` : `${rounded} ${unitFor(kind)}`;
}

/** "or more" / "or less" / "or faster" / "or slower" — the comparison's plain-language direction. */
function comparisonDirectionPhrase(comparison: GoalComparison): string {
  switch (comparison) {
    case 'at_least':
      return 'or more';
    case 'at_most':
      return 'or less';
    case 'faster_or_equal':
      return 'or faster';
    case 'slower_or_equal':
      return 'or slower';
    default:
      return '';
  }
}

/**
 * "Traveled 1.6 body heights — your target was 1.4 or more." / "Hit 150° —
 * your target was 150° or more." The verb varies slightly by kind so the
 * sentence reads naturally; the structure (measured — target + direction)
 * is the same for every kind, matching the mockup's own three worked
 * examples (Distance, Speed, and — generalized here — Angle).
 */
export function formatGoalMeasuredVsTarget(item: GoalScoreItem): string | null {
  if (item.measured == null || item.target == null) return null;
  const verb = item.kind === 'speed' ? 'Hit' : item.kind === 'angle' ? 'Reached' : 'Traveled';
  const measuredStr = formatMeasurement(item.measured, item.kind);
  const targetStr = formatMeasurement(item.target, item.kind);
  const direction = comparisonDirectionPhrase(item.comparison);
  return `${verb} ${measuredStr} — your target was ${targetStr}${direction ? ` ${direction}` : ''}.`;
}

// ---------------------------------------------------------------------------
// Per-Goal card state + bonus line
// ---------------------------------------------------------------------------

export type GoalCardState = 'exceeded' | 'met' | 'missed' | 'cannot_measure';

/**
 * Derives the card's visual state. A Goal can be beaten, not just passed
 * (F §3) — four states instead of a Check's three:
 *   - abstained -> "Could not measure"
 *   - bonus > 0 -> "Exceeded"
 *   - bonus === 0 (measured, met/matched exactly or close enough that the
 *     bar was reached but not beaten) -> "Met"
 *   - measured but no bonus AND the comparison direction says it fell
 *     short -> "Missed"
 *
 * `bonus === 0` alone cannot distinguish Met from Missed (both contribute
 * zero to the sum) — the mockup's own worked examples distinguish them by
 * whether `measured` actually reached the bar in the comparison's
 * direction, so that is what this function checks.
 */
export function goalCardState(item: GoalScoreItem): GoalCardState {
  if (item.status === 'abstained' || item.score == null) return 'cannot_measure';
  if (item.bonus != null && item.bonus > 0) return 'exceeded';
  if (item.measured == null || item.target == null) return 'met'; // measured but no comparable value — treat as neutral "met"
  const met =
    item.comparison === 'at_least' || item.comparison === 'slower_or_equal'
      ? item.measured >= item.target
      : item.measured <= item.target;
  return met ? 'met' : 'missed';
}

export const GOAL_CARD_STATE_LABEL: Record<GoalCardState, string> = {
  exceeded: 'Exceeded',
  met: 'Met',
  missed: 'Missed',
  cannot_measure: 'Could not measure',
};

/**
 * The bonus-points sentence under the measured-vs-target line. Never
 * renders `null`/`undefined` as "0 bonus points" — a `null` bonus means
 * "not measured," handled entirely by `goalCardAbstentionCopy` instead.
 */
export function goalCardBonusLine(item: GoalScoreItem, state: GoalCardState): string | null {
  if (state === 'cannot_measure') return null;
  if (state === 'exceeded' && item.bonus != null) {
    const rounded = Math.round(item.bonus * 10) / 10;
    return `+${rounded} bonus point${rounded === 1 ? '' : 's'}`;
  }
  if (state === 'met') {
    return 'You matched it — no bonus points here, same as a regular check.';
  }
  // missed
  return 'No bonus points here.';
}

/**
 * WHAT/WHY/HOW for a "Could not measure" Goal card. REUSES
 * `withheldCheckpointReason` (the same copy a withheld Check uses) rather
 * than duplicating a second reason table, then appends the Goal-specific
 * reassurance the v5 mockup states explicitly ("Nothing to do; this won't
 * lower your score") — a Goal is bonus-only, so an unmeasured one can never
 * cost the athlete anything on Form, which the reused Check copy alone
 * doesn't say.
 */
export function goalCardAbstentionCopy(item: GoalScoreItem): string {
  const reason = (item.abstention_reason ?? 'evaluation_error') as GoalAbstentionReason;
  const base = withheldCheckpointReason(reason, item.body_part);
  return `${base} This won't affect your Form or Goal score.`;
}

// ---------------------------------------------------------------------------
// Hero block — Goal pill + Total arithmetic
// ---------------------------------------------------------------------------

/**
 * "2 Goals exceeded, 1 met, 1 missed, 1 couldn't be measured" — fully
 * accounted for (every goal in the list lands in exactly one bucket), per
 * the v5 mockup's own corrected string (red-team amendment 9: the v1 mockup
 * string didn't sum).
 */
export function goalBreakdownSubline(goals: GoalScoreItem[]): string {
  const counts = { exceeded: 0, met: 0, missed: 0, cannot_measure: 0 };
  for (const g of goals) counts[goalCardState(g)] += 1;

  const parts: string[] = [];
  if (counts.exceeded > 0) parts.push(`${counts.exceeded} Goal${counts.exceeded === 1 ? '' : 's'} exceeded`);
  if (counts.met > 0) parts.push(`${counts.met} met`);
  if (counts.missed > 0) parts.push(`${counts.missed} missed`);
  if (counts.cannot_measure > 0) {
    parts.push(`${counts.cannot_measure} couldn't be measured`);
  }
  return parts.join(', ');
}

/** "No Goal set on this template." — the `None` case, never rendered as 0 (LFT-063). */
export function noGoalConfiguredCopy(): string {
  return 'No Goal set on this template.';
}

/**
 * The case where this template HAS a Goal, but none of them could be
 * measured from THIS video (every item in `goals[]` abstained). Distinct
 * from `noGoalConfiguredCopy` — that copy is only accurate when no
 * Goal-eligible checkpoint exists on the template at all.
 */
export function goalsUnmeasuredCopy(): string {
  return "We couldn't measure a Goal from this video, so there's no Goal bonus or Total score this time. Your Form score above is unaffected.";
}

/**
 * Goal Score go-live B4 — the exact-zero bonus sentence. Shown ONLY when
 * every configured Goal was measured (no abstentions) and the summed Goal
 * bonus is EXACTLY `0.0` — a true "met every bar, beat none of them"
 * result, not a measurement failure (which is `goalsUnmeasuredCopy`'s
 * territory, a `null` bonus). `Total = goal_bonus_score x form_effect`
 * would otherwise literally multiply out to `0` here (not `formScore`),
 * which reads as "you failed everything" to an athlete who, in fact, hit
 * every single target. The hero shows this sentence instead of the
 * arithmetic line, with Total displayed equal to Form.
 */
export function exactZeroGoalBonusCopy(): string {
  return 'You met every goal, right on target';
}

/**
 * Owner ruling G-2 (2026-10-29, red-team finding F3, option A) on the
 * exact-zero hero: the sentence above is shown ALONGSIDE the honest
 * numbers (bonus 0, Total 0), never in place of them — an earlier design
 * substituted `formScore` for the real (zero) Total, which let the
 * stored/ranked Total look like it had silently become the Form score.
 * This caption sits next to the "+0" bonus pill specifically so that 0 can
 * never be misread as the Form score shown under the separate Total label.
 */
export function exactZeroBonusPointsLabel(): string {
  return "0 bonus points — the Total score below is 0 too, not your Form score.";
}

/**
 * "Goals 12 × Form effect 1.88 (from Form 84 on this template's curve) =
 * Total 22.6" — the v5 mockup's exact arithmetic line, matching the REAL
 * formula (`Total = goal_bonus_score × form_effect`, REPORT.md §2.6), not
 * the superseded v1 "Form + Goal" additive line some earlier design docs
 * still show.
 */
export function totalArithmeticLine(
  goalBonusScore: number,
  formEffect: number,
  formScore: number,
  totalScore: number,
): string {
  const b = Math.round(goalBonusScore * 10) / 10;
  const m = Math.round(formEffect * 100) / 100;
  const t = Math.round(totalScore * 10) / 10;
  const f = Math.round(formScore);
  return `Goals ${b} × Form effect ${m} (from Form ${f} on this template's curve) = Total ${t}`;
}

/**
 * "Goals 18 × Form effect 0.92 = 16.6" — the leaderboard row's own, shorter
 * sub-line (`J_leaderboard_v2.md` §1.3), distinct from `totalArithmeticLine`
 * above: no "(from Form N on this template's curve)" clause (the leaderboard
 * row shows Total next to the sub-line already, so that clause would be
 * redundant there). Same rounding convention as §1.3 specifies: Goals to one
 * decimal, Form effect to two, Total to one. Returns `null` when any of the
 * three inputs is `null` — `form_effect`/`goal_bonus_score`/`total_score`
 * are a published package; a Form-ranked board (or a legacy row) sends all
 * three as `null`, and that is the caller's cue to hide the sub-line
 * entirely rather than render a broken equation (LFT-063: `null` is never
 * `0`).
 */
export function leaderboardGoalBreakdownLine(
  goalBonusScore: number | null,
  formEffect: number | null,
  totalScore: number | null,
): string | null {
  if (goalBonusScore == null || formEffect == null || totalScore == null) return null;
  const b = Math.round(goalBonusScore * 10) / 10;
  const m = Math.round(formEffect * 100) / 100;
  const t = Math.round(totalScore * 10) / 10;
  return `Goals ${b} × Form effect ${m} = Total ${t}`;
}

/** "New personal best!" / "Your last best Total on this template was 21.1 — this one is 22.6." */
export function personalBestBannerCopy(previousBest: number, current: number): { title: string; detail: string } {
  const prev = Math.round(previousBest * 10) / 10;
  const now = Math.round(current * 10) / 10;
  return {
    title: 'New personal best!',
    detail: `Your last best Total on this template was ${prev} — this one is ${now}.`,
  };
}

/** The filming-tip box shown near publish, on both Results and Leaderboard (red team amendment 18). */
export function filmingTipCopy(): string {
  return (
    "Goal scores are sensitive to how you film. Use the same camera spot, angle, and distance as your " +
    'coach\'s demo each time, so your Goal scores stay comparable session to session.'
  );
}

// ---------------------------------------------------------------------------
// Publish consent
// ---------------------------------------------------------------------------

/** Description shown with the consent question, before an answer is given. */
export function consentDescription(channelName: string): string {
  return (
    `Other athletes in ${channelName} would be able to see your Total score and your name next to it. ` +
    "They won't see your video, your Checks detail, or your coach's notes — just the score and your name."
  );
}

export function consentDefaultNote(): string {
  return 'Nothing is shared until you choose Yes. You can change this any time from this same spot.';
}

export function publishedVisibleCopy(channelName: string): string {
  return `Visible to ${channelName} now.`;
}

/** Shown when the channel-level opt-in kill switch is off — the toggle is disabled, not hidden. */
export function consentDisabledCopy(): string {
  return 'Turn on "Show me on the leaderboard" in your channel settings first, then come back here.';
}

/**
 * The permanent helper text under a published score, distinct from the
 * backend's own `notice` (shown once, right after a hide action succeeds).
 */
export function publishedHelperCopy(): string {
  return 'Hiding this score removes it from the leaderboard — we keep the evaluation privately either way.';
}
